import { FormField } from "./FormField";
import { Box, Flex, Pressable } from "../../../mads";
import { Close } from "../../../mads/icons";
import { useFocus } from "../../../mads/util/useFocus";

import { colors } from "@indeed/ifl-tokens";
import { type ComponentProps, type ReactElement, useCallback, useEffect, useRef } from "react";
import { Platform, StyleSheet, TextInput, type ViewStyle } from "react-native";

const styles = StyleSheet.create({
  inputWrapper: {
    borderRadius: 8,
    borderColor: colors.border["[base]"],
    borderWidth: 1,
    textAlignVertical: "top",
    flexDirection: "row",
    alignItems: "center",
  },
  focusedInputWrapper: {
    borderColor: colors.border.primary["[base]"],
    boxShadow:
      Platform.OS === "web"
        ? `${colors.utility.focus["[base]"]} 0px -3px 0px 0px inset`
        : undefined,
  },
  input: {
    minHeight: 40,
    padding: 9,
    flex: 1,
    fontFamily: "Indeed Sans",
    outlineWidth: Platform.OS === "web" ? 0 : undefined,
  },
  closeIcon: {
    marginVertical: 7,
    marginRight: 12,
    position: "absolute",
    right: 0,
  },
});

interface InputProps {
  /**
   * identifies that the field is disabled.
   */
  disabled?: boolean;

  /**
   * identifies if to show the clear icon to clear input
   */
  clearable?: boolean;

  /**
   * Styles the field content to indicate that it is invalid.
   */
  invalid?: boolean;

  /**
   * The label text for the field.
   */
  label?: string;

  /**
   * The error text for the field when invalid is `true` (renders below the children).
   */
  errorText?: string;

  /**
   * The helper text for the field (renders below the label).
   */
  helperText?: string;

  /**
   * Size of the form field. Use the values `sm` or `md`. Defaults to 'md'.
   */
  size?: "sm" | "md";

  /**
   * content to show at the start of the input - e.g. an icon
   */
  contentBefore?: ReactElement;

  /**
   * The current value of the text field
   */
  value: string;

  /**
   * Called when text is input
   */
  onInput: (text: string) => void;

  /**
   * Called when input loses focus
   */
  onBlur?: () => void;

  /**
   * Called when input gets focused
   */
  onFocus?: () => void;

  /**
   * Whether to focus the input on mount
   */
  focusOnMount: boolean;

  /**
   * The text to show when there is no value
   */
  placeholder?: string;

  /**
   * The accessibility label for the close icon
   */
  clearIconLabel?: string;

  /**
   * The accessibility role for the input
   */
  TextInputProps: ComponentProps<typeof TextInput>;

  /**
   * Optional style overrides merged onto the input's bordered wrapper
   * (e.g. height, borderRadius). Useful for consumers that need a specific
   * field size without affecting other usages.
   */
  inputWrapperStyle?: ViewStyle;
}

/**
 * @see https://experience.indeed.design/components/ids/input
 */
export function Input({
  invalid,
  disabled,
  label,
  helperText,
  errorText,
  size = "md",
  value,
  contentBefore,
  onInput,
  clearable = false,
  onFocus,
  onBlur,
  focusOnMount,
  placeholder,
  clearIconLabel,
  TextInputProps,
  inputWrapperStyle,
}: InputProps) {
  const inputRef = useRef<TextInput>(null);
  const { handleFocus, handleBlur, isFocused } = useFocus({
    onFocus,
    onBlur,
  });

  const handleOnClear = useCallback(() => {
    onInput("");
    // The TextInput is now uncontrolled; needs to be cleared imperatively
    if (Platform.OS === "web" && inputRef.current) {
      // @ts-expect-error For web, the ref is an HTMLInputElement with a value property
      inputRef.current.value = "";
    }
  }, [onInput]);

  useEffect(() => {
    // The TextInput is uncontrolled (uses `defaultValue` to avoid cursor
    // flicker on re-render), so prop-driven value updates — e.g. a
    // suggestion selection that calls `setAutoCompleteValue(...)` upstream
    // — don't reach the rendered field unless we sync the DOM value
    // imperatively here. Only updates when the prop and DOM disagree, so
    // user typing isn't disturbed (RNW's onChangeText already keeps the
    // DOM and prop in sync for keystrokes).
    if (Platform.OS === "web" && inputRef.current) {
      const inputEl = inputRef.current as unknown as { value: string };
      if (inputEl.value !== value) {
        inputEl.value = value;
      }
    }
  }, [value]);

  useEffect(() => {
    // This is a workaround for a bug in React Native where the keyboard does not show up on Android when autoFocus is set to true
    // and the input is not focused on mount in the Modal. This will keep trying to focus the input until the keyboard is visible.
    // This is a temporary fix until the bug is fixed in React Native.
    if (Platform.OS === "android" && focusOnMount) {
      const timeout = setTimeout(() => {
        inputRef?.current?.blur();
        inputRef?.current?.focus();
      }, 150);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [focusOnMount]);

  return (
    <FormField
      helperText={helperText}
      errorText={errorText}
      aria-describedby={helperText}
      invalid={invalid}
      disabled={disabled}
      label={label}
      size={size}
    >
      <Flex
        sx={{
          ...inputWrapperStyle,
          ...styles.inputWrapper,
          ...(isFocused && styles.focusedInputWrapper),
        }}
      >
        {contentBefore && <Box sx={{ paddingLeft: 8 }}>{contentBefore}</Box>}
        <TextInput
          ref={inputRef}
          onChangeText={onInput}
          style={styles.input}
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoFocus={focusOnMount}
          placeholder={placeholder}
          placeholderTextColor={colors.foreground.weak}
          // Using defaultValue instead of value is a workaround for a known issue that value & cursor flickers during rerender. Fixed in https://code.corp.indeed.com/mobile-app-platform/react-native/-/merge_requests/734.
          defaultValue={value}
          {...TextInputProps}
        />
        {clearable && value ? (
          <CloseIcon onClear={handleOnClear} clearIconLabel={clearIconLabel} />
        ) : null}
      </Flex>
    </FormField>
  );
}

export function CloseIcon({ onClear, clearIconLabel }: CloseIconProps) {
  return (
    <Pressable
      sx={styles.closeIcon}
      onPress={onClear}
      accessibilityRole="button"
      accessibilityLabel={clearIconLabel}
    >
      <Close size="md" color={colors.foreground.strong} aria-hidden />
    </Pressable>
  );
}

export interface CloseIconProps {
  onClear: () => void;
  clearIconLabel?: string;
}
